import { platformFeaturesAtom } from "@framework/shared/atoms";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import { Eye, Heart, MessageCircle } from "lucide-react";
import type { Short } from "../../types";
import { formatCompactCount } from "../../utils/format-compact-count";

/**
 * Compact thumbnail card used in rails (home + category) — the entry point
 * to the share/single-view page.
 */
type ShortCardProps = {
  short: Short;
};

export function ShortCard({ short }: ShortCardProps) {
  const href = short.canonicalUrl ?? `/shorts/${short.id}`;
  const commentsEnabled =
    platformFeaturesAtom.useValue().commentsAndRatings === true;

  return (
    <Link
      to={href}
      className="group relative block aspect-[9/16] w-40 flex-shrink-0 overflow-hidden rounded-xl bg-neutral-900 shadow-md transition-transform hover:scale-[1.02] sm:w-48"
    >
      {short.thumbnail?.url ? (
        <img
          src={short.thumbnail.url}
          alt={short.title}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full items-center justify-center text-3xl text-white/40">
          🎬
        </div>
      )}

      {/* gradient overlay for legibility */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

      <div className="absolute inset-x-2 bottom-2 text-white">
        <p className="line-clamp-2 text-sm font-semibold leading-snug">
          {short.title}
        </p>
        {short.instructor?.name && (
          <p className="mt-1 text-xs text-white/75">{short.instructor.name}</p>
        )}
        <div className="mt-1 flex items-center gap-3 text-[11px] text-white/80">
          <span className="inline-flex items-center gap-1">
            <Eye size={12} />
            {formatCompactCount(short.views ?? 0)}
          </span>
          {commentsEnabled ? (
            <>
              <span className="inline-flex items-center gap-1">
                <Heart size={12} />
                {short.likes ?? 0}
              </span>
              <span className="inline-flex items-center gap-1">
                <MessageCircle size={12} />
                {short.comments ?? 0}
              </span>
            </>
          ) : null}
        </div>
      </div>

      <span className="sr-only">{trans("shorts.openShort")}</span>
    </Link>
  );
}
